import APIClient from '../neoverv/config/APIClient';
import { config } from '../neoverv/config/Setting';
import Autoload from '../neoverv/Autoload';
import dayjs from 'dayjs';

// The sibling ReceiptTenantTransactions/Transaction/{id} reverse-lookup answers "nothing
// mapped" with a 4xx and {message: 'NOT_FOUND'} rather than 200 + [] (see PaymentsList.jsx's
// handleGetRecieptsForTransaction), so treat that the same as an empty list, not an error.
function isNotFoundError(error) {
  const status = error?.response?.status;
  const message = error?.response?.data?.message ?? error?.response?.data?.error;
  return status === 404 || message === 'NOT_FOUND';
}

async function fetchMappings(url) {
  try {
    const response = await APIClient({ method: 'GET', url, headers: { Authorization: Autoload.Utils.getAuthToken() } });
    return Array.isArray(response.data?.result) ? response.data.result : [];
  } catch (error) {
    if (isNotFoundError(error)) return [];
    throw error;
  }
}

async function fetchTransaction(transactionId) {
  const response = await APIClient({
    method: 'GET',
    url: `${config.transactionById}${transactionId}`,
    headers: { Authorization: Autoload.Utils.getAuthToken() },
  });
  return response.data?.result;
}

// Mirrors the real Transaction's embedded assignment shape (see AssignTransactionModal's
// componentDidMount), built from the mapping row itself — this is always called for one
// specific receipt or one specific tenant+month, so no reverse lookup is needed.
function withAssignments(transaction, mapping) {
  return {
    ...transaction,
    transactionAssigments: {
      receiptIds: mapping.receiptId != null ? [mapping.receiptId] : [],
      tenantId: mapping.tenantId ?? null,
      rentalMonth: mapping.rentalMonth ?? null,
    },
  };
}

async function fetchTransactionsForMappings(mappings) {
  const transactions = await Promise.all(mappings.map((mapping) => fetchTransaction(mapping.transactionId)));
  return transactions.map((transaction, i) => withAssignments(transaction, mappings[i]));
}

export async function fetchTransactionsForReceipt({ propertyId, receiptId }) {
  const query = new URLSearchParams({ PropertyId: propertyId, ReceiptId: receiptId });
  const mappings = await fetchMappings(`${config.receiptTenantTransactionsByReceipt}?${query}`);
  return fetchTransactionsForMappings(mappings);
}

export async function fetchTransactionsForRentalPayment({ propertyId, tenantId, rentalPaymentId, rentalMonth }) {
  const query = new URLSearchParams({
    PropertyId: propertyId,
    TenantId: tenantId,
    RentalPaymentId: rentalPaymentId,
    RentalMonth: dayjs(rentalMonth).format('YYYY-MM-DD'),
  });
  const mappings = await fetchMappings(`${config.receiptTenantTransactionsByTenant}?${query}`);
  return fetchTransactionsForMappings(mappings);
}

// Removes one transaction's assignment to a receipt or to a tenant — the same endpoints
// PaymentsList and AssignTransactionModal already use.
export async function deleteTransactionAssignment({ transactionId, receiptId, tenantId }) {
  const target = receiptId != null ? `Receipt/${receiptId}` : `Tenant/${tenantId}`;
  const response = await APIClient({
    method: 'DELETE',
    url: `${config.receiptTenantTransactionsByTransaction}${transactionId}/${target}`,
    headers: { Authorization: Autoload.Utils.getAuthToken() },
  });
  if (response.data?.status === false) {
    throw new Error(response.data?.message || 'Removing the assignment was rejected');
  }
}
